import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
    getCulture,
    getOwnerPage,
    getCalendarSize,
    actionCreators as calendarActionCreators,
} from "page/calendar/store";
import { getOwnerPage as getOwnerPageType } from "burde-api-content/store";
import { ApplicationState } from "store";
import {
    getCroppedCustomOwnerPageImage,
    actionCreators as uiActionCreators,
    getUploadedOwnerPageImages,
} from "ui/store";
import { actionCreators as eventActionCreators } from "event/store";
import { getOwnerPageImageOptions } from "ui/service";
import * as styles from "./owner-page.css";
import { ConnectedImageUploader as ImageUploader } from "./owner-page-image-uploader";
import OwnerInformationForm from "./owner-information-form";
import OwnerPageImageGallery from "./owner-page-image-gallery";
import { CustomImage, OnSaveResponse, OwnerPageInfo } from "burde";
import { uploadOwnerPageImage } from "./service";
import { getOwnerPageCustomImageInfoFromId } from "calendar/service";
import { getAppInsights } from "util/telemetry-service";
import { Spinner } from "common/spinner/spinner";
import { ConnectedOwnerPageSelector as OwnerPageSelector } from "./owner-page-selector";
import ConfirmationModal from "common/confirmation-modal";
import Button from "content/button/button";
import { useResources } from "contexts/resource-context";

type OwnerPageProps = {
    culture: string;
    calendarSize: string;
    ownerPage: OwnerPageInfo;
    croppedCustomOwnerPageImage: CustomImage | null;
    uploadedOwnerPageImages: CustomImage[];
} & typeof calendarActionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators;

const OwnerPage: React.FC<OwnerPageProps> = ({
    culture,
    calendarSize,
    ownerPage,
    croppedCustomOwnerPageImage,
    updateOwnerPage,
    updateCroppedCustomOwnerPageImage,
    updateOwnerPageImageOptions,
    updateUserHasLeftOwnerPageStep,
    updateUploadedOwnerPageImages,
    uploadedOwnerPageImages,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

    const resources = useResources();

    const getErrorResponse = () => {
        return {
            success: false,
            errorMessage:
                "The file could not be uploaded. Try decreasing the file size.",
        };
    };

    // Emit event when user leaves (handled in calendar.tsx, for saving temporary calendar)
    useEffect(() => {
        return () => {
            updateUserHasLeftOwnerPageStep(true);
        };
    }, []);

    useEffect(() => {
        if (
            ownerPage.customImageId !== null &&
            croppedCustomOwnerPageImage == null
        ) {
            setIsLoading(true);
            getOwnerPageCustomImageInfoFromId(ownerPage.customImageId).then(
                (result) => {
                    if (result !== null) {
                        updateCroppedCustomOwnerPageImage(result);
                    }
                    setIsLoading(false);
                },
            );
        }
        setIsLoading(true);
        // Owner page image options
        getOwnerPageImageOptions(culture, calendarSize).then((response) => {
            if (response !== null) {
                updateOwnerPageImageOptions(response);
            }
            setIsLoading(false);
        });
    }, []);

    const saveOwnerImage = async (blob: Blob): Promise<OnSaveResponse> => {
        try {
            const response = await uploadOwnerPageImage(blob);

            if (response === null) {
                const ai = getAppInsights();

                ai.trackEvent({
                    name: "SaveOwnerImageException-IdIsNull",
                    properties: {
                        ownerPage,
                        blob,
                    },
                });

                setShowErrorModal(true);

                return getErrorResponse();
            }

            const updatedOwnerPage = {
                ...ownerPage,
                customImageId: response.id,
            };

            updateOwnerPage(updatedOwnerPage);
            updateCroppedCustomOwnerPageImage(response);

            return {
                success: true,
            };
        } catch (e) {
            const ai = getAppInsights();

            ai.trackEvent({
                name: "SaveOwnerImageException",
                properties: {
                    ownerPage,
                    blob,
                },
            });

            return getErrorResponse();
        }
    };

    const onSelectCallback = async (imageUrl: string | null) => {
        if (!imageUrl) {
            updateOwnerPage({
                ...ownerPage,
                customImageId: null,
            });

            return { success: true };
        }

        // Select callback
        const image = uploadedOwnerPageImages.find((x) => x.url === imageUrl);
        if (!image) return { success: false };

        updateOwnerPage({
            ...ownerPage,
            customImageId: image.id,
        });

        return { success: true };
    };

    const updateOwnerPageLayoutId = async (pageId: number) => {
        ownerPage.layoutId = pageId;
        updateOwnerPage(ownerPage);
    };

    const removeUploadedImageCallback = (imageUrl: string) => {
        const updatedImages = [...uploadedOwnerPageImages].filter(
            (x) => x.url !== imageUrl,
        );

        updateUploadedOwnerPageImages(updatedImages);
    };

    return (
        <div className={styles.inputForm}>
            {isLoading && (
                <div
                    style={{
                        width: "100%",
                        paddingTop: "50px",
                        display: "flex",
                        justifyContent: "center",
                    }}>
                    <Spinner />
                </div>
            )}

            {!isLoading && (
                <>
                    <OwnerPageSelector
                        updateOwnerPageLayoutId={updateOwnerPageLayoutId}
                    />
                    <OwnerInformationForm />
                    <ImageUploader
                        onSaveCallback={saveOwnerImage}
                        onSelectCallback={onSelectCallback}
                        ownerPage={ownerPage}
                        onRemoveCallback={removeUploadedImageCallback}
                    />
                    <OwnerPageImageGallery />
                </>
            )}

            {showErrorModal && (
                <ConfirmationModal
                    isOpen={showErrorModal}
                    title={resources["Cover_Image_UploadError"]}
                    message=""
                    content={
                        <div>
                            <Button
                                onClick={() => setShowErrorModal(false)}
                                text={
                                    resources["Cover_Image_UploadErrorConfirm"]
                                }
                            />
                        </div>
                    }
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    culture: getCulture(state),
    calendarSize: getCalendarSize(state),
    ownerPage: getOwnerPage(state),
    croppedCustomOwnerPageImage: getCroppedCustomOwnerPageImage(state),
    uploadedOwnerPageImages: getUploadedOwnerPageImages(state),
});

const mapDispatchToProps = {
    ...uiActionCreators,
    ...calendarActionCreators,
    ...eventActionCreators,
};

export default connect(mapStateToProps, mapDispatchToProps)(OwnerPage);
